import React from "react";
import "@/App.css";
import "@/machines.css";
import "@/gateway.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

import Landing from "@/pages/Landing";
import Gate from "@/pages/Gate";
import Questionnaire from "@/pages/Questionnaire";
import Submitted from "@/pages/Submitted";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminShell from "@/pages/admin/AdminShell";
import DealBoard from "@/pages/admin/DealBoard";
import ReviewQueue from "@/pages/admin/ReviewQueue";
import FixtureBench from "@/pages/admin/FixtureBench";
import Analytics from "@/pages/admin/Analytics";
import EmailInbox from "@/pages/admin/EmailInbox";
import ContentAdmin from "@/pages/admin/ContentAdmin";
import ContactInbox from "@/pages/admin/ContactInbox";
import OnboardingAdmin from "@/pages/admin/OnboardingAdmin";
import Onboarding from "@/pages/onboarding/Onboarding";

// Marketing pages (enterprise side)
import MarketingHome from "@/pages/marketing/Home";
import MarketingSolution from "@/pages/marketing/Solution";
import MarketingAbout from "@/pages/marketing/About";
import MarketingResearch from "@/pages/marketing/Research";
import MarketingBlog from "@/pages/marketing/Blog";
import MarketingContact from "@/pages/marketing/Contact";

// Gateway (Part 6, gated by SHOW_GATEWAY)
import Gateway from "@/pages/Gateway";
import Gateway2 from "@/pages/Gateway2";
import Enterprise2Home from "@/pages/enterprise2/Home";
import Enterprise2Solution from "@/pages/enterprise2/Solution";
import Enterprise2About from "@/pages/enterprise2/About";
import Enterprise2Research from "@/pages/enterprise2/Research";
import Enterprise2Blog from "@/pages/enterprise2/Blog";
import Enterprise2Contact from "@/pages/enterprise2/Contact";
import Enterprise2Atom from "@/pages/enterprise2/Atom";
import Machines2Home from "@/pages/machines2/Home";
import Machines2Products from "@/pages/machines2/Products";
import Machines2Approach from "@/pages/machines2/Approach";
import Machines2Contact from "@/pages/machines2/Contact";
import { Machines2Industries, Machines2Industry } from "@/pages/machines2/Industries";
import { P2Layout } from "@/components/plan2/P2Layout";
import Plan2Landing from "@/pages/plan2/Landing";
import Plan2Gate from "@/pages/plan2/Gate";
import Plan2Questionnaire from "@/pages/plan2/Questionnaire";
import Plan2Submitted from "@/pages/plan2/Submitted";
import Login2 from "@/pages/Login2";
import Machines, { MachinesContact } from "@/pages/Machines";
import {
  MachinesProducts,
  MachinesApproach,
  MachinesIndustries,
  MachinesIndustry,
} from "@/pages/MachinesPages";
import RouteLoader from "@/components/RouteLoader";
import "@/components/RouteLoader.css";
import "@/machines-responsive.css";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import ComingSoon from "@/pages/ComingSoon";
import PrivateBeta from "@/pages/PrivateBeta";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";

// v2 build (Parts 5–7 of the master build prompt)
import { SHOW_GATEWAY, SHOW_MACHINES_ARM } from "@/config/flags";

/** Whether the whole site is currently gated behind the /coming-soon page.
 *  Toggle via REACT_APP_COMING_SOON=true on the frontend env (preview or prod).
 *  Admin routes remain accessible so operations aren't blocked. */
const COMING_SOON_MODE =
  (process.env.REACT_APP_COMING_SOON || "").toString().toLowerCase() === "true";

/**
 * Preserve query string when redirecting legacy paths to their canonical
 * counterpart. Resume links, bookmarks and shared URLs continue to resolve
 * exactly as they did before.
 */
function RedirectWithQuery({ to }) {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search}${loc.hash}`} replace />;
}

/**
 * Root route.
 * Serves Gateway 2 (the redesigned site). The legacy Gateway stays at
 * /gateway and the holding page at /coming-soon for reference.
 */
function RootRoute() {
  return <PrivateBeta />;
}

function MachinesIndustryRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/machines-2/industries/${slug}`} replace />;
}

/** Legacy path -> duplicate-site path, preserving the query string (e.g. ?return_to=). */
function LegacyRedirect({ to }) {
  const loc = useLocation();
  return <Navigate to={`${to}${loc.search}`} replace />;
}

/** Machines-side guard. Renders 404 when the arm is off (Part 5.2). */
function MachinesRoute({ children }) {
  if (!SHOW_MACHINES_ARM) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <RouteLoader />
      <Routes>
        {/* Coming-soon holding page (always reachable at /coming-soon) */}
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/private-beta" element={<PrivateBeta />} />

        {/* Auth — always reachable so OAuth flow can complete regardless of coming-soon mode. */}
        <Route path="/login-legacy" element={<Login />} />
        <Route path="/login-2" element={<Login2 />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/onboarding" element={<Onboarding track="ceo" />} />
        <Route path="/onboarding/technical" element={<Onboarding track="tech" />} />

        {/* When COMING_SOON_MODE is on, every public route serves the holding
            page. Admin routes stay live so operations continue. */}
        {COMING_SOON_MODE ? (
          <>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminShell />}>
              <Route index element={<Navigate to="/admin/deal-board" replace />} />
              <Route path="deal-board" element={<DealBoard />} />
              <Route path="queue" element={<ReviewQueue />} />
              <Route path="fixtures" element={<FixtureBench />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="emails" element={<EmailInbox />} />
              <Route path="content" element={<ContentAdmin />} />
              <Route path="contact" element={<ContactInbox />} />
              <Route path="onboarding" element={<OnboardingAdmin />} />
            </Route>
            <Route path="*" element={<ComingSoon />} />
          </>
        ) : (
          <></>
        )}

        {/* Full site — used when COMING_SOON_MODE is off. */}
        {!COMING_SOON_MODE && (
          <>
        {/* Root — coming-soon holding page (temporary) */}
        <Route path="/" element={<RootRoute />} />

        {/* Legacy entry points -> the redesigned duplicate site */}
        <Route path="/login" element={<LegacyRedirect to="/login-2" />} />
        <Route path="/enterprise" element={<LegacyRedirect to="/enterprise-2" />} />
        <Route path="/enterprise/solution" element={<LegacyRedirect to="/enterprise-2/solution" />} />
        <Route path="/enterprise/about" element={<LegacyRedirect to="/enterprise-2/about" />} />
        <Route path="/enterprise/research" element={<LegacyRedirect to="/enterprise-2/research" />} />
        <Route path="/enterprise/blog" element={<LegacyRedirect to="/enterprise-2/blog" />} />
        <Route path="/enterprise/contact" element={<LegacyRedirect to="/enterprise-2/contact" />} />
        <Route path="/machines" element={<LegacyRedirect to="/machines-2" />} />
        <Route path="/machines/products" element={<LegacyRedirect to="/machines-2/products" />} />
        <Route path="/machines/industries" element={<LegacyRedirect to="/machines-2/industries" />} />
        <Route path="/machines/industries/:slug" element={<MachinesIndustryRedirect />} />
        <Route path="/machines/approach" element={<LegacyRedirect to="/machines-2/approach" />} />
        <Route path="/machines/contact" element={<LegacyRedirect to="/machines-2/contact" />} />
        <Route path="/plan" element={<LegacyRedirect to="/plan-2" />} />
        <Route path="/plan/gate" element={<LegacyRedirect to="/plan-2/gate" />} />
        <Route path="/plan/questionnaire" element={<LegacyRedirect to="/plan-2/questionnaire" />} />
        <Route path="/plan/resume" element={<LegacyRedirect to="/plan-2/resume" />} />
        <Route path="/plan/submitted" element={<LegacyRedirect to="/plan-2/submitted" />} />
        {/* Gateway still previewable & editable while root is locked */}
        <Route path="/gateway" element={<Gateway />} />
        <Route path="/gateway-2" element={<Gateway2 />} />

        {/* Enterprise (relocated marketing) — canonical paths */}
        <Route path="/enterprise-legacy" element={<MarketingHome />} />
        <Route path="/enterprise-legacy/solution" element={<MarketingSolution />} />
        <Route path="/enterprise-legacy/about" element={<MarketingAbout />} />
        <Route path="/enterprise-legacy/research" element={<MarketingResearch />} />
        <Route path="/enterprise-legacy/blog" element={<MarketingBlog />} />
        <Route path="/enterprise/blog/*" element={<LegacyRedirect to="/enterprise-2/blog" />} />
        <Route path="/enterprise-legacy/blog/*" element={<MarketingBlog />} />
        <Route path="/enterprise-legacy/contact" element={<MarketingContact />} />

        {/* Enterprise 2 — independent duplicate of /enterprise/* (own components + CSS) */}
        <Route path="/enterprise-2" element={<Enterprise2Home />} />
        <Route path="/enterprise-2/solution" element={<Enterprise2Solution />} />
        <Route path="/enterprise-2/atom" element={<Enterprise2Atom />} />
        <Route path="/enterprise-2/about" element={<Enterprise2About />} />
        <Route path="/enterprise-2/research" element={<Enterprise2Research />} />
        <Route path="/enterprise-2/blog" element={<Enterprise2Blog />} />
        <Route path="/enterprise-2/blog/*" element={<Enterprise2Blog />} />
        <Route path="/enterprise-2/contact" element={<Enterprise2Contact />} />

        {/* Machines 2 — independent duplicate of /machines/*, restyled to the Enterprise 2 system */}
        <Route path="/machines-2" element={<Machines2Home />} />
        <Route path="/machines-2/products" element={<Machines2Products />} />
        <Route path="/machines-2/industries" element={<Machines2Industries />} />
        <Route path="/machines-2/industries/:slug" element={<Machines2Industry />} />
        <Route path="/machines-2/approach" element={<Machines2Approach />} />
        <Route path="/machines-2/contact" element={<Machines2Contact />} />

        {/* Plan 2 — duplicated assessment flow in the Enterprise 2 skin */}
        <Route path="/plan-2" element={<P2Layout><Plan2Landing /></P2Layout>} />
        <Route path="/plan-2/gate" element={<P2Layout><Plan2Gate /></P2Layout>} />
        <Route path="/plan-2/questionnaire" element={<P2Layout><Plan2Questionnaire /></P2Layout>} />
        <Route path="/plan-2/resume" element={<P2Layout><Plan2Questionnaire /></P2Layout>} />
        <Route path="/plan-2/submitted" element={<P2Layout><Plan2Submitted /></P2Layout>} />

        {/* Legacy marketing paths → new /enterprise/* canonicals (five 301s + blog wildcard) */}
        <Route path="/solution" element={<RedirectWithQuery to="/enterprise/solution" />} />
        <Route path="/about"    element={<RedirectWithQuery to="/enterprise/about" />} />
        <Route path="/research" element={<RedirectWithQuery to="/enterprise/research" />} />
        <Route path="/blog"     element={<RedirectWithQuery to="/enterprise/blog" />} />
        <Route path="/blog/*"   element={<RedirectWithQuery to="/enterprise/blog" />} />
        <Route path="/contact"  element={<RedirectWithQuery to="/enterprise/contact" />} />

        {/* Machines side — guarded by SHOW_MACHINES_ARM. Placeholder routes; the
            /machines page and /machines/contact ship in a later step. */}
        <Route
          path="/machines-legacy"
          element={
            <MachinesRoute>
              <Machines />
            </MachinesRoute>
          }
        />
        <Route
          path="/machines-legacy/contact"
          element={
            <MachinesRoute>
              <MachinesContact />
            </MachinesRoute>
          }
        />
        <Route
          path="/machines-legacy/products"
          element={
            <MachinesRoute>
              <MachinesProducts />
            </MachinesRoute>
          }
        />
        <Route
          path="/machines-legacy/approach"
          element={
            <MachinesRoute>
              <MachinesApproach />
            </MachinesRoute>
          }
        />
        <Route
          path="/machines-legacy/industries"
          element={
            <MachinesRoute>
              <MachinesIndustries />
            </MachinesRoute>
          }
        />
        <Route
          path="/machines-legacy/industries/:slug"
          element={
            <MachinesRoute>
              <MachinesIndustry />
            </MachinesRoute>
          }
        />

        {/* Audit — canonical paths under /plan (Part 2.1 fence — do not move) */}
        <Route path="/plan-legacy" element={<Landing />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/plan-legacy/gate" element={<Gate />} />
        <Route path="/plan-legacy/questionnaire" element={<Questionnaire />} />
        <Route path="/plan-legacy/resume" element={<Questionnaire />} />
        <Route path="/plan-legacy/submitted" element={<Submitted />} />

        {/* Legacy /audit paths — preserve query string for signed resume tokens */}
        <Route path="/audit" element={<RedirectWithQuery to="/plan/gate" />} />
        <Route path="/audit/questionnaire" element={<RedirectWithQuery to="/plan/questionnaire" />} />
        <Route path="/audit/resume" element={<RedirectWithQuery to="/plan/resume" />} />
        <Route path="/audit/submitted" element={<RedirectWithQuery to="/plan/submitted" />} />

        {/* Admin */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminShell />}>
          <Route index element={<Navigate to="/admin/deal-board" replace />} />
          <Route path="deal-board" element={<DealBoard />} />
          <Route path="queue" element={<ReviewQueue />} />
          <Route path="fixtures" element={<FixtureBench />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="emails" element={<EmailInbox />} />
          <Route path="content" element={<ContentAdmin />} />
          <Route path="contact" element={<ContactInbox />} />
          <Route path="onboarding" element={<OnboardingAdmin />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
}

export default App;
